import {
  pgTable,
  uuid,
  text,
  integer,
  jsonb,
  timestamp,
  index,
  uniqueIndex,
  unique,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Actor types for event attribution
// ---------------------------------------------------------------------------

export const ACTOR_TYPES = [
  "user",
  "system",
  "automation",
  "api",
  "import",
] as const;

export type ActorType = (typeof ACTOR_TYPES)[number];

// ---------------------------------------------------------------------------
// Domain Records — Immutable Event Log (Two-Tier Model, Tier 1)
// ---------------------------------------------------------------------------
// Source of truth for all domain events. Written in the same DB transaction
// as the state mutation. Per-entity sequence numbers for event sourcing.
// Per-tenant global sequence for reconciliation gap detection.

export const domainRecords = pgTable(
  "domain_records",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // SCHEMA: should be .notNull() — nullable tenantId is a smell for tenant-scoped event queries
    tenantId: uuid("tenant_id"),
    type: text("type").notNull(),
    entityType: text("entity_type").notNull(),
    entityId: uuid("entity_id").notNull(),
    sequenceNumber: integer("sequence_number").notNull(),
    tenantSequence: integer("tenant_sequence"),
    actorId: text("actor_id").notNull(),
    actorType: text("actor_type").notNull(),
    payload: jsonb("payload")
      .$type<Record<string, unknown>>()
      .notNull()
      .default({}),
    causationId: uuid("causation_id"),
    correlationId: uuid("correlation_id").notNull(),
    metadata: jsonb("metadata")
      .$type<{
        schemaVersion: number;
        source: string;
        ip?: string;
        userAgent?: string;
      }>()
      .notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index("idx_domain_records_entity_seq").on(
      table.entityId,
      table.sequenceNumber,
    ),
    index("idx_domain_records_type").on(table.type),
    index("idx_domain_records_created").on(table.createdAt),
    index("idx_domain_records_tenant_seq").on(
      table.tenantId,
      table.tenantSequence,
    ),
    uniqueIndex("uq_domain_records_action_materialized_causation")
      .on(table.tenantId, table.type, table.causationId)
      .where(sql`${table.causationId} IS NOT NULL AND ${table.type} = 'crm.action_recommendation.materialized'`),
    // AN-027 Phase 3 follow-up to PR #2735 (Codex P1 on PR #2739).
    // Race-safe CAPI consent suppression dedupe — concurrent retries that
    // both observe an empty SELECT will now collide on this index instead
    // of inserting twice. `correlation_id` is NOT NULL today, but the
    // partial predicate keeps the index aligned with the dedupe contract
    // (only enforces uniqueness when a correlation id is present) and
    // lets the column evolve without invalidating the index.
    uniqueIndex("uq_domain_records_tenant_type_correlation")
      .on(table.tenantId, table.type, table.correlationId)
      .where(sql`${table.correlationId} IS NOT NULL`),
    unique("uq_domain_records_entity_seq").on(
      table.entityId,
      table.sequenceNumber,
    ),
    check(
      "chk_domain_records_actor_type",
      sql`${table.actorType} IN ('user', 'system', 'automation', 'api', 'import')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Entity Sequences — Per-Entity Monotonic Counter
// ---------------------------------------------------------------------------
// Fast sequence generation without scanning domain_records.
// Atomically incremented via INSERT ... ON CONFLICT DO UPDATE.

export const entitySequences = pgTable("entity_sequences", {
  entityId: uuid("entity_id").primaryKey(),
  lastSequence: integer("last_sequence").notNull().default(0),
});

// ---------------------------------------------------------------------------
// Tenant Event Counter — Global Per-Tenant Sequence
// ---------------------------------------------------------------------------
// Single-row table per tenant DB. Used by the reconciliation worker to
// detect gaps between DomainRecords and EventNotifications.
// Atomically incremented in the SAME transaction as DomainRecord insert.

export const tenantEventCounter = pgTable(
  "tenant_event_counter",
  {
    id: integer("id").primaryKey().default(1),
    lastSequence: integer("last_sequence").notNull().default(0),
  },
  (table) => [check("single_row_check", sql`${table.id} = 1`)],
);
